import { NetworkStatus } from '@apollo/client';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';

import { snackbar } from '@occc-app/design-system';
import { LinkItem } from '@occc/ui-design-system';
import { Flow_Types, FormContainerProps } from '@occc-app/offerHelpers/types';
import { useUserSelector } from '@occc-app/App/AppContext';
import { getOfferComponentType, getTitleByOfferType } from '@occc-app/offerHelpers/utils';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
import { useGetOfferById } from '@occc-app/dataAccessLayer/offer/queries';
import { useGetInitialFormProps } from '@occc-app/offerHelpers/hooks/useGetInitialFormProps';
import { CampaignComponentTabs } from '@occc-app/routes/CampaignComponents/components/ConfigurationLayout';
import copies from '@occc-app/routes/CampaignComponents/copies';
import { getOfferFetchPolicyFromNavigationState } from '@occc-app/routes/OfferRedirect/utils';
import { OfferType } from '@occc-app/offerValidationService/types';
import { useChainId } from '@occc-app/Chain/hooks';
import { OfferFragment, User_Role } from '@occc/graphql';

import { useGetScheduleFormProps } from '../ScheduleForm/hooks';

type GetFormContainerPropsArgs = {
  campaignId?: string;
  offerId: string;
  offerDetailsRoute: string;
  onCancel: (hasUnsavedChanges: boolean) => void;
  currentFlowType: Flow_Types;
  offerType: OfferType;
  getBreadcrumbLinks: (offerName: string) => LinkItem[];
  ignoreConfigureFormSubmit?: boolean;
  getIsOrderAheadEnabled?: (offer: OfferFragment | undefined) => boolean;
};

export const useGetFormContainerProps = ({
  campaignId,
  offerId,
  onCancel,
  currentFlowType,
  offerDetailsRoute,
  offerType,
  getBreadcrumbLinks,
  ignoreConfigureFormSubmit = false,
  getIsOrderAheadEnabled,
}: GetFormContainerPropsArgs) => {
  const chainId = useChainId();

  const navigate = useNavigate();
  const location = useLocation();
  const { pathname } = location;
  const offerFetchPolicy = getOfferFetchPolicyFromNavigationState(location.state);
  const user = useUserSelector();
  const isReadOnlyAdminRole = user?.userRole === User_Role.AdminReadOnly;

  const tabValue = useMemo(() => {
    const tabValues = Object.values(CampaignComponentTabs);
    const match = tabValues.find((value) => pathname.endsWith(value));
    return match || CampaignComponentTabs.Configure;
  }, [pathname]);

  const setTabValue = (value: CampaignComponentTabs) => {
    navigate(value);
  };

  const {
    data: offer,
    loading: offerLoading,
    networkStatus,
    refetch: refetchOfferQuery,
  } = useGetOfferById(offerId, chainId, {
    fetchPolicy: offerFetchPolicy,
  });

  const silentRefetchCountRef = useRef(0);
  const isSilentRefetching = silentRefetchCountRef.current > 0;
  const isRefetching = networkStatus === NetworkStatus.refetch;

  const onRefetchOffer = useCallback(
    async (silent?: boolean) => {
      if (silent) {
        silentRefetchCountRef.current += 1;
      }
      try {
        await refetchOfferQuery();
      } finally {
        if (silent) {
          silentRefetchCountRef.current = Math.max(0, silentRefetchCountRef.current - 1);
        }
      }
    },
    [refetchOfferQuery],
  );

  const onError = useCallback(
    (error: Error) => {
      snackbar.error(getErrorMessage(error));
      return onRefetchOffer();
    },
    [onRefetchOffer],
  );

  const setConfigureTab = () => {
    setTabValue(CampaignComponentTabs.Configure);
  };

  const onInitialFormSubmitted = async (isOfferUpdated: boolean) => {
    await onRefetchOffer();

    if (isOfferUpdated) {
      snackbar.success(copies.onSubmitSuccessToastMessage);
    }

    setConfigureTab();
  };

  const initialFormProps = useGetInitialFormProps(
    offerId,
    offer,
    onInitialFormSubmitted,
    onError,
    setConfigureTab,
  );

  const onScheduleFormSubmitted = async () => {
    await onRefetchOffer();
    snackbar.success(copies.onSubmitSuccessToastMessage);

    if (currentFlowType === Flow_Types.Configure) {
      navigate(offerDetailsRoute);
    }
  };

  const scheduleFormProps = useGetScheduleFormProps({
    offerId,
    offer,
    onFormSubmitted: onScheduleFormSubmitted,
    onError,
    onCancel,
    currentFlowType,
    type: offerType,
  });

  const title = useMemo(() => {
    const offerTitle = getTitleByOfferType(offerType);

    if (isReadOnlyAdminRole) {
      return `${offerTitle} Configurations`;
    } else if (
      tabValue === CampaignComponentTabs.Configure &&
      currentFlowType !== Flow_Types.Update
    ) {
      return `Create a New ${offerTitle}`;
    }

    return offer?.name || '';
  }, [tabValue, isReadOnlyAdminRole, offer?.name, currentFlowType, offerType]);

  const layoutProps = {
    title,
    selectedTab: tabValue,
    onTabSelected: setTabValue,
    disabled: ignoreConfigureFormSubmit ? false : offer?.earningMechanism === null,
    campaignComponentType: offer ? getOfferComponentType(offer) : null,
    breadcrumbLinks: getBreadcrumbLinks(offer?.name || ''),
    offerId,
    loading: offerLoading && !isSilentRefetching,
  };

  const showOrderAheadToggle = getIsOrderAheadEnabled ? getIsOrderAheadEnabled(offer) : false;

  const formContainersProps: FormContainerProps = {
    campaignId,
    offerId,
    offer,
    offerLoading: offerLoading && !isSilentRefetching,
    onRefetchOffer,
    isRefetching: isSilentRefetching && isRefetching,
    setTabValue,
    currentFlowType,
    onCancel,
    onError,
    showOrderAheadToggle,
    type: offerType,
  };

  return {
    layoutProps,
    initialFormProps,
    scheduleFormProps,
    formContainersProps,
  };
};
